import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { adminApi, type UserAccount, type UserStatus } from '@/lib/api'

const searchSchema = z.object({
  query: z.string().min(1, 'Search query is required'),
})

type SearchFormData = z.infer<typeof searchSchema>

export function UsersPage() {
  const [searchQuery, setSearchQuery] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const queryClient = useQueryClient()

  const { register, handleSubmit } = useForm<SearchFormData>({
    resolver: zodResolver(searchSchema),
  })

  const { data: usersData, isLoading } = useQuery({
    queryKey: ['users', searchQuery, currentPage],
    queryFn: () =>
      adminApi.users.list({
        search: searchQuery,
        page: currentPage,
        perPage: 20,
      }),
  })

  const suspendMutation = useMutation({
    mutationFn: ({
      userId,
      action,
    }: {
      userId: string
      action: 'suspend' | 'unsuspend'
    }) => adminApi.users.updateStatus(userId, action),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] })
    },
  })

  function onSearch(data: SearchFormData) {
    setSearchQuery(data.query)
    setCurrentPage(1)
  }

  function handleStatusToggle(userId: string, currentStatus: UserStatus) {
    const action = currentStatus === 'active' ? 'suspend' : 'unsuspend'
    suspendMutation.mutate({ userId, action })
  }

  function getStatusBadgeClasses(status: UserStatus): string {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800'
      case 'suspended':
        return 'bg-red-100 text-red-800'
      case 'pending':
        return 'bg-yellow-100 text-yellow-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="px-8 py-6">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
        <p className="mt-1 text-sm text-gray-500">
          View and manage user accounts. Only phone hashes and usernames are
          displayed — no plaintext phone numbers.
        </p>
      </div>

      {/* Search */}
      <form onSubmit={handleSubmit(onSearch)} className="mb-6">
        <div className="flex space-x-3">
          <input
            type="text"
            placeholder="Search by username or phone hash..."
            className="flex-1 rounded-lg border border-gray-300 px-4 py-2 text-sm shadow-sm placeholder:text-gray-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
            {...register('query')}
          />
          <button
            type="submit"
            className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2"
          >
            Search
          </button>
        </div>
      </form>

      {/* Users table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Username
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Phone Hash
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Registered
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  Last Active
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {usersData?.users.map((user: UserAccount) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                    {user.username}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 font-mono text-xs text-gray-500">
                    {user.phoneHash.substring(0, 16)}...
                  </td>
                  <td className="whitespace-nowrap px-6 py-4">
                    <span
                      className={`inline-flex rounded-full px-2 py-1 text-xs font-semibold ${getStatusBadgeClasses(user.status)}`}
                    >
                      {user.status}
                    </span>
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                    {new Date(user.registeredAt).toLocaleDateString()}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                    {new Date(user.lastActiveAt).toLocaleDateString()}
                  </td>
                  <td className="whitespace-nowrap px-6 py-4 text-right">
                    <button
                      onClick={() =>
                        handleStatusToggle(user.id, user.status)
                      }
                      disabled={suspendMutation.isPending}
                      className={`rounded-md px-3 py-1.5 text-xs font-medium shadow-sm transition-colors disabled:opacity-50 ${
                        user.status === 'active'
                          ? 'border border-red-300 bg-red-50 text-red-700 hover:bg-red-100'
                          : 'border border-green-300 bg-green-50 text-green-700 hover:bg-green-100'
                      }`}
                    >
                      {user.status === 'active' ? 'Suspend' : 'Unsuspend'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination */}
          {usersData && usersData.totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-6 py-3">
              <p className="text-sm text-gray-500">
                Page {currentPage} of {usersData.totalPages} (
                {usersData.total} users)
              </p>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                  className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() =>
                    setCurrentPage((p) =>
                      Math.min(usersData.totalPages, p + 1),
                    )
                  }
                  disabled={currentPage >= usersData.totalPages}
                  className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
